import { WifiOff } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { getMe, listGroupsWithItems, NetworkError } from "../api/client";
import { useGroup } from "../contexts/GroupContext";
import { useItemsCache } from "../contexts/ItemsCacheContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import { setAnalyticsUserId } from "../lib/analytics";
import { getUserId, isLoggedIn, setUserInfo } from "../lib/auth";

const AppInitContext = createContext<{
  fetchAppData: () => Promise<void>;
} | null>(null);

export function useAppInit() {
  const ctx = useContext(AppInitContext);
  if (!ctx) throw new Error("useAppInit must be used within AppInitializer");
  return ctx;
}

export function AppInitializer({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation("common");
  const { activeGroupId, initializeGroups } = useGroup();
  const { setItems: setCacheItems } = useItemsCache();
  const { setIsPro, setAuthProviders } = useSubscription();
  const pending = useRef<Promise<void> | null>(null);
  const activeGroupIdRef = useRef(activeGroupId);
  activeGroupIdRef.current = activeGroupId;
  const [initError, setInitError] = useState<"network" | "timeout" | null>(
    null,
  );

  const fetchAppData = useCallback(async () => {
    if (pending.current) return pending.current;

    const promise = (async () => {
      const [me, groupsWithItems] = await Promise.all([
        getMe(),
        listGroupsWithItems(),
      ]);

      setUserInfo({
        displayName: me.displayName ?? "",
        pictureUrl: me.pictureUrl ?? null,
        isPro: me.isPro,
        authProviders: me.authProviders,
      });
      setIsPro(me.isPro);
      setAuthProviders(me.authProviders);

      if (groupsWithItems.length > 0) {
        const currentActive = activeGroupIdRef.current;
        const preservedId =
          currentActive && groupsWithItems.some((g) => g.id === currentActive)
            ? currentActive
            : groupsWithItems[0].id;
        initializeGroups(
          groupsWithItems.map((g) => ({ id: g.id, name: g.name })),
          preservedId,
        );
        for (const group of groupsWithItems) {
          setCacheItems(group.items, group.id);
        }
      } else {
        initializeGroups([], undefined);
      }
    })();

    pending.current = promise;
    try {
      await promise;
    } finally {
      pending.current = null;
    }
  }, [initializeGroups, setCacheItems, setIsPro, setAuthProviders]);

  // ページリロード時: トークンがあればデータ取得
  useEffect(() => {
    if (isLoggedIn()) {
      const userId = getUserId();
      if (userId) {
        setAnalyticsUserId(userId);
      }
      setInitError(null);
      fetchAppData().catch((err) => {
        if (err instanceof NetworkError) {
          setInitError(err.message === "timeout" ? "timeout" : "network");
        }
        // 401 は api/client.ts の自動ログアウトに任せる
      });
    }
  }, [fetchAppData]);

  function handleRetry() {
    setInitError(null);
    fetchAppData().catch((err) => {
      if (err instanceof NetworkError) {
        setInitError(err.message === "timeout" ? "timeout" : "network");
      }
    });
  }

  if (initError) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 px-6">
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-50">
            <WifiOff size={32} className="text-orange-400" />
          </div>
          <h2 className="mb-2 text-lg font-semibold text-gray-800">
            {t("error.title")}
          </h2>
          <p className="mb-6 text-sm text-gray-500">
            {initError === "timeout" ? t("error.timeout") : t("error.network")}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="rounded-lg bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-600"
          >
            {t("buttons.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <AppInitContext.Provider value={{ fetchAppData }}>
      {children}
    </AppInitContext.Provider>
  );
}
